class Api {
  constructor() {
    this.language = document.documentElement.lang
    this.base = `/${this.language}/api/`

    document.addEventListener('DOMContentLoaded', () => {
      this.csrf = document.getElementsByName('csrfmiddlewaretoken')[0]?.value
    })
  }

  request(path, opts, base) {
    return fetch((base ? base : this.base) + path, opts)
      .then((response) => {
        if (response.ok) return response

        return Promise.reject(response)
      })
      .catch(async (response) => {
        let error

        try {
          error = await response.json()
        } catch (error) {
          // console.log(error)
          return Promise.reject('Eroare necunoscută')
        }

        return Promise.reject(
          error.message || error.error || error.detail || error,
        )
      })
  }

  post(path, body, opts) {
    return this.request(path, {
      ...opts,
      body: JSON.stringify(body),
      method: 'POST',
      headers: {
        'X-CSRFToken': this.csrf,
        'Content-Type': 'application/json',
      },
    }).then(this.processResponse)
  }

  patch(path, opts) {
    return this.request(path, {
      ...opts,
      method: 'PATCH',
      headers: {
        'X-CSRFToken': this.csrf,
        'Content-Type': 'application/json',
      },
    }).then(this.processResponse)
  }

  put(path, opts) {
    return this.request(path, {
      ...opts,
      method: 'PUT',
      headers: {
        'X-CSRFToken': this.csrf,
        'Content-Type': 'application/json',
      },
    }).then(this.processResponse)
  }

  delete(path, opts) {
    return this.request(path, {
      ...opts,
      method: 'DELETE',
      headers: {
        'X-CSRFToken': this.csrf,
      },
    }).then(this.processResponse)
  }

  get(path, opts) {
    return this.request(path, opts).then(this.processResponse)
  }

  async processResponse(response) {
    if (!response.ok) {
      return response.text()
    }

    const text = await response.text()
    if (!text) {
      return null
    }

    // Try to parse as JSON, fallback to text if it fails
    try {
      return JSON.parse(text)
    } catch (e) {
      return text
    }
  }

  // Users API
  getUsers(params = {}) {
    const searchParams = new URLSearchParams(params)
    return this.get(`users/?${searchParams}`)
  }

  getUser(id) {
    return this.get(`users/${id}/`)
  }

  createUser(userData) {
    return this.post('users/', userData)
  }

  updateUser(id, userData) {
    return this.patch(`users/${id}/`, { body: JSON.stringify(userData) })
  }

  deleteUser(id) {
    return this.delete(`users/${id}/`)
  }

  // User Actions
  getUserMe() {
    return this.get('users/me/')
  }

  getUserReservations() {
    return this.get('users/my-reservations/')
  }

  changePassword(passwordData) {
    return this.post('users/change-password/', passwordData)
  }

  getUserProfiles() {
    return this.get('users/my-profiles/')
  }

  createUserProfile(profileData) {
    return this.post('users/my-profiles/', profileData)
  }

  getUserProfile(id) {
    return this.get(`users/my-profiles/${id}/`)
  }

  updateUserProfile(id, profileData) {
    return this.put(`users/my-profiles/${id}/`, {
      body: JSON.stringify(profileData),
    })
  }

  deleteUserProfile(id) {
    return this.delete(`users/my-profiles/${id}/`)
  }

  logoutUser() {
    return this.post('users/logout/')
  }

  deleteUserAccount() {
    return this.post('users/delete-account/')
  }

  // Events API
  getEvents(params = {}) {
    const searchParams = new URLSearchParams(params)
    return this.get(`events/?${searchParams}`)
  }

  getEvent(id) {
    return this.get(`events/${id}/`)
  }

  // People API
  getPeople(params = {}) {
    const searchParams = new URLSearchParams(params)
    return this.get(`people/?${searchParams}`)
  }

  getPerson(id) {
    return this.get(`people/${id}/`)
  }

  // Articles API
  getArticles(params = {}) {
    const searchParams = new URLSearchParams(params)
    return this.get(`articles/?${searchParams}`)
  }

  getArticle(id) {
    return this.get(`articles/${id}/`)
  }

  // Explore API
  getExplore(params = {}) {
    const searchParams = new URLSearchParams(params)
    return this.get(`explore/?${searchParams}`)
  }

  // Open Calls API
  getOpenCalls(params = {}) {
    const searchParams = new URLSearchParams(params)
    return this.get(`open-calls/?${searchParams}`)
  }

  getOpenCall(id) {
    return this.get(`open-calls/${id}/`)
  }

  // Team Members API
  getTeamMembers(params = {}) {
    const searchParams = new URLSearchParams(params)
    return this.get(`team-members/?${searchParams}`)
  }

  getTeamMember(id) {
    return this.get(`team-members/${id}/`)
  }

  // Shop API
  getShopItems(params = {}) {
    const searchParams = new URLSearchParams(params)
    return this.get(`shop/?${searchParams}`)
  }

  getShopItem(id) {
    return this.get(`shop/${id}/`)
  }

  // Bookings Dashboard API
  getDashboard(params = {}) {
    const searchParams = new URLSearchParams(params)
    return this.get(`bookings/dashboard/?${searchParams}`)
  }

  // Bookings Reservations API
  getReservations(params = {}) {
    const searchParams = new URLSearchParams(params)
    return this.get(`bookings/reservations/?${searchParams}`)
  }

  getReservation(id) {
    return this.get(`bookings/reservations/${id}/`)
  }

  createReservation(reservationData) {
    return this.post('bookings/reservations/', reservationData)
  }

  // Reservation Actions
  acceptReservation(id, orderId = null) {
    return this.get(
      `bookings/reservations/${id}/accept/` + (orderId ? orderId : ''),
    )
  }

  rejectReservation(id) {
    return this.post(`bookings/reservations/${id}/reject/`)
  }

  updateReservation(id, reservationData) {
    return this.patch(`bookings/reservations/${id}/`, {
      body: JSON.stringify(reservationData),
    })
  }

  deleteReservation(id) {
    return this.delete(`bookings/reservations/${id}/`)
  }

  // Bookings Orders API
  getOrders(params = {}) {
    const searchParams = new URLSearchParams(params)
    return this.get(`bookings/orders/?${searchParams}`)
  }

  getOrder(id) {
    return this.get(`bookings/orders/${id}/`)
  }

  createOrder(orderData) {
    return this.post('bookings/orders/', orderData)
  }

  updateOrder(id, orderData) {
    return this.put(`bookings/orders/${id}/`, {
      body: JSON.stringify(orderData),
    })
  }

  deleteOrder(id) {
    return this.delete(`bookings/orders/${id}/`)
  }

  // Order Actions
  createOrderInvoice(id, invoiceData) {
    return this.post(`bookings/orders/${id}/create_invoice/`, invoiceData)
  }

  createOrderContract(id, contractData) {
    return this.post(
      `bookings/orders/${id}/create-document/contract/`,
      contractData,
    )
  }

  generateOrderOffer(id) {
    return this.post(`bookings/orders/${id}/create-document/offer/`)
  }

  // Bookings Resources API
  getResources(params = {}) {
    const searchParams = new URLSearchParams(params)
    return this.get(`bookings/resources/?${searchParams}`)
  }

  getResource(id) {
    return this.get(`bookings/resources/${id}/`)
  }

  createResource(resourceData) {
    return this.post('bookings/resources/', resourceData)
  }

  updateResource(id, resourceData) {
    return this.put(`bookings/resources/${id}/`, {
      body: JSON.stringify(resourceData),
    })
  }

  deleteResource(id) {
    return this.delete(`bookings/resources/${id}/`)
  }

  // Bookings Spaces API
  getSpaces(params = {}) {
    const searchParams = new URLSearchParams(params)
    return this.get(`bookings/spaces/?${searchParams}`)
  }

  getSpace(id) {
    return this.get(`bookings/spaces/${id}/`)
  }

  createSpace(spaceData) {
    return this.post('bookings/spaces/', spaceData)
  }

  updateSpace(id, spaceData) {
    return this.put(`bookings/spaces/${id}/`, {
      body: JSON.stringify(spaceData),
    })
  }

  deleteSpace(id) {
    return this.delete(`bookings/spaces/${id}/`)
  }

  getSpaceAvailability(params = {}) {
    const searchParams = new URLSearchParams(params)
    return this.get(`bookings/space-availability/?${searchParams}`)
  }

  getSpaceAvailabilityDaily(params = {}) {
    const searchParams = new URLSearchParams(params)
    return this.get(`bookings/space-availability/daily/?${searchParams}`)
  }

  // Bookings Prices API
  getPrices(params = {}) {
    const searchParams = new URLSearchParams(params)
    return this.get(`bookings/prices/?${searchParams}`)
  }

  getPrice(id) {
    return this.get(`bookings/prices/${id}/`)
  }

  createPrice(priceData) {
    return this.post('bookings/prices/', priceData)
  }

  updatePrice(id, priceData) {
    return this.patch(`bookings/prices/${id}/`, {
      body: JSON.stringify(priceData),
    })
  }

  deletePrice(id) {
    return this.delete(`bookings/prices/${id}/`)
  }

  // Bookings Clients API
  getClients(params = {}) {
    const searchParams = new URLSearchParams(params)
    return this.get(`bookings/clients/?${searchParams}`)
  }

  getClient(id) {
    return this.get(`bookings/clients/${id}/`)
  }

  createClient(clientData) {
    return this.post('bookings/clients/', clientData)
  }

  updateClient(id, clientData) {
    return this.put(`bookings/clients/${id}/`, {
      body: JSON.stringify(clientData),
    })
  }

  deleteClient(id) {
    return this.delete(`bookings/clients/${id}/`)
  }

  // Client Actions
  getClientByCode(params = {}) {
    const searchParams = new URLSearchParams(params)
    return this.get(`bookings/clients/by-code/?${searchParams}`)
  }

  // Bookings Programs API
  getPrograms(params = {}) {
    const searchParams = new URLSearchParams(params)
    return this.get(`bookings/programs/?${searchParams}`)
  }

  getProgram(id) {
    return this.get(`bookings/programs/${id}/`)
  }

  createProgram(programData) {
    return this.post('bookings/programs/', programData)
  }

  updateProgram(id, programData) {
    return this.put(`bookings/programs/${id}/`, {
      body: JSON.stringify(programData),
    })
  }

  deleteProgram(id) {
    return this.delete(`bookings/programs/${id}/`)
  }

  // Bookings Choices API
  getBookingChoices() {
    return this.get('bookings/choices/')
  }

  // Bookings Stats API
  getBookingStats(params = {}) {
    const searchParams = new URLSearchParams(params)
    return this.get(`bookings/stats/?${searchParams}`)
  }

  getBookingEvents(spaces) {
    const params = spaces ? `?spaces_ids=${spaces}` : ''
    return this.get(`bookings/events/${params}`)
  }

  // Search API
  getSearch(params) {
    const searchParams = new URLSearchParams(params)
    return this.get(`search/?${searchParams}`)
  }
}

export default new Api()
